import { PlusOutlined } from '@ant-design/icons';
import { Button, Heading } from '@mm/components';
import { XRayIconSquare } from '@mm/icons';
import { selectAciveCrms } from '@mm/state/selectors/connectionsSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import { ReactComponent as LogoIcon } from 'assets/favicon.svg';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ArcherContainer, ArcherElement } from 'react-archer';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import {
  IntegrationListItem,
  INTEGRATIONS_LIST,
} from '../../Integrations/constants';
import {
  IntegrationIcon,
  integrationsIconClassNames,
} from '../../Integrations/IntegrationIcon/IntegrationIcon';
import styles from './SourcesDestinationsWidget.module.css';
import { useResolutionInfo } from '@mm/hooks';

export const SourcesDestinationsWidget = () => {
  const facebookPage = useSelector(activeFacebookPageSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const activeCrms = useSelector(selectAciveCrms);
  const hasActiveCrms = !!activeCrms.length;
  const isXRayActivated = facebookPage?.getemailsActivated;
  const history = useHistory();

  const attachmentRef = useRef(null);

  const { screenWidth } = useResolutionInfo();
  const isTabletOrSmaller = screenWidth <= 900;

  const sourcesTypes = ['csv_file', 'manual_suppression', 'suppression'];

  const sources: IntegrationListItem[] = [
    {
      type: 'xray',
      title: 'X-Ray Pixel',
      subtitle:
        'This allows us to identify and track anonymous New Contacts and known Existing Contacts when they visit your website',
    },
    ...INTEGRATIONS_LIST.filter(
      integration =>
        sourcesTypes.includes(integration.type) &&
        activeCrms?.includes(integration.type),
    ),
  ];

  const destinations: IntegrationListItem[] = hasActiveCrms
    ? INTEGRATIONS_LIST.filter(
        integration =>
          !sourcesTypes.includes(integration.type) &&
          activeCrms?.includes(integration.type),
      )
    : INTEGRATIONS_LIST.filter(
        i => i.type === 'klaviyo' || i.type === 'facebook_ads_audience',
      );

  const navigateToIntegrations = () =>
    history.push(BotBuilderRoutes.integrations.getLink({ chatbotId }));

  const navigateToXRay = () =>
    history.push(BotBuilderRoutes.xRay.getLink({ chatbotId }));

  const navigateToContacts = () =>
    history.push(BotBuilderRoutes.contacts.getLink({ chatbotId }));

  const navigateToSignal = () =>
    history.push(BotBuilderRoutes.signal.getLink({ chatbotId }));

  const navigateToXRayAnalytics = () =>
    history.push(BotBuilderRoutes.xrayAnalytics.getLink({ chatbotId }));

  return (
    <div className={styles.wrapper}>
      <Heading size={4} className={styles.heading}>
        Capture visitor data to sync with your marketing stack
      </Heading>
      <ArcherContainer strokeColor="#8ea1b2" strokeWidth={2}>
        <div className={styles.container}>
          <div className={styles.sourcesContainer}>
            <div className={styles.title}>Sources</div>
            <Button
              leftIcon={<PlusOutlined style={{ fontSize: 16 }} />}
              onClick={navigateToIntegrations}
              disabled={!isXRayActivated}
              css={{ container: styles.primaryButton }}
            >
              Source
            </Button>
            {sources.map(source => {
              const activeCRM = activeCrms.includes(source.type);
              const isXRay = source.type === 'xray';
              return (
                <ArcherElement
                  key={source.type}
                  id={`source-${source.type}`}
                  relations={
                    isXRay
                      ? [
                          {
                            targetId: 'attachment',
                            targetAnchor: !isTabletOrSmaller ? 'left' : 'top',
                            sourceAnchor: !isTabletOrSmaller
                              ? 'right'
                              : 'bottom',
                            style: { endMarker: false },
                          },
                        ]
                      : []
                  }
                >
                  <div
                    key={source.type}
                    className={classnames(
                      styles.sectionWrapper,
                      styles.sourcesWrapper,
                      {
                        [styles.xrayInactive]: isXRay && !isXRayActivated,
                      },
                    )}
                  >
                    <div className={styles.section} key={source.type}>
                      {activeCRM && (
                        <span
                          className={classnames(
                            styles.status,
                            styles.activeStatus,
                          )}
                        >
                          Active
                        </span>
                      )}
                      <div
                        className={classnames(
                          'integrations__header-icon',
                          integrationsIconClassNames[source.type],
                        )}
                      >
                        <IntegrationIcon
                          integrationType={source.type}
                          iconProps={{
                            size: 24,
                            color:
                              source.type === 'mailchimp' ? '#262626' : 'white',
                          }}
                          isActive
                        />
                        {isXRay && <XRayIconSquare color="white" />}
                      </div>

                      <div className={styles.sectionContent}>
                        <div className={styles.sectionTitle}>
                          {source.title}
                        </div>
                        <div className={styles.sectionSubtitle}>
                          {source.subtitle}
                        </div>
                        {isXRay && !isXRayActivated && (
                          <Button
                            css={{ container: styles.primaryButton }}
                            onClick={navigateToXRay}
                          >
                            Install X-Ray
                          </Button>
                        )}
                      </div>
                    </div>
                    {isXRay && isXRayActivated && (
                      <div className={styles.buttonsRow}>
                        <Button
                          color="secondary-blue"
                          css={{ container: styles.secondaryButton }}
                          onClick={navigateToContacts}
                        >
                          New Contacts
                        </Button>
                        <Button
                          color="secondary-blue"
                          css={{ container: styles.secondaryButton }}
                          onClick={navigateToSignal}
                        >
                          My Events
                        </Button>
                        <Button
                          color="secondary-blue"
                          css={{ container: styles.secondaryButton }}
                          onClick={navigateToXRayAnalytics}
                        >
                          Analytics
                        </Button>
                      </div>
                    )}
                  </div>
                </ArcherElement>
              );
            })}

            {sources.length > 1 && (
              <Button color="secondary-blue" onClick={navigateToIntegrations}>
                Go to Active Sources ({sources.length}){' '}
              </Button>
            )}
          </div>
          <ArcherElement
            id="attachment"
            relations={destinations
              .filter((d, idx) => (isTabletOrSmaller ? idx === 0 : true))
              .map(d => ({
                targetId: `dest-${d.type}`,
                targetAnchor: !isTabletOrSmaller ? 'left' : 'top',
                sourceAnchor: !isTabletOrSmaller ? 'right' : 'bottom',
                style: { endMarker: false },
              }))}
          >
            <div className={styles.attachmentPoint} ref={attachmentRef}>
              <LogoIcon />
            </div>
          </ArcherElement>

          <div className={styles.destinationsContainer}>
            <div className={styles.title}>Destinations</div>
            <Button
              leftIcon={<PlusOutlined style={{ fontSize: 16 }} />}
              onClick={navigateToIntegrations}
              disabled={!isXRayActivated}
              css={{ container: styles.primaryButton }}
            >
              Destination
            </Button>
            {destinations
              .filter((_, idx) => idx < 3)
              .map(destination => (
                <ArcherElement
                  key={destination.type}
                  id={`dest-${destination.type}`}
                >
                  <div
                    key={destination.type}
                    className={classnames(
                      styles.sectionWrapper,
                      styles.sourcesWrapper,
                      { [styles.inactiveWidget]: !hasActiveCrms },
                    )}
                  >
                    <div className={styles.section} key={destination.type}>
                      {hasActiveCrms && (
                        <span
                          className={classnames(
                            styles.status,
                            styles.activeStatus,
                          )}
                        >
                          Active
                        </span>
                      )}
                      <div
                        className={classnames(
                          'integrations__header-icon',
                          integrationsIconClassNames[destination.type],
                        )}
                      >
                        <IntegrationIcon
                          integrationType={destination.type}
                          iconProps={{
                            size: 24,
                            color:
                              destination.type === 'mailchimp'
                                ? '#262626'
                                : 'white',
                          }}
                          isActive
                        />
                      </div>

                      <div className={styles.sectionContent}>
                        <div className={styles.sectionTitle}>
                          {destination.title}
                        </div>
                        <div className={styles.sectionSubtitle}>
                          {destination.subtitle}
                        </div>
                      </div>
                    </div>
                  </div>
                </ArcherElement>
              ))}

            {hasActiveCrms && destinations.length > 1 && (
              <Button color="secondary-blue" onClick={navigateToIntegrations}>
                Go to Active Destinations ({destinations.length}){' '}
              </Button>
            )}
          </div>
        </div>
      </ArcherContainer>
    </div>
  );
};
